'use client'

import { analyticsDisabled, captureEvent, identifyUser } from '@/lib/analytics'
import logger from '@/lib/logger'

interface AnalyticsEvent {
  // Navigation events
  social_link_click: {
    platform: 'twitter' | 'github' | 'linkedin' | 'instagram' | 'photos'
    location: 'header' | 'footer' | 'home'
  }

  // Project events
  project_view: {
    project_name: string
    source: 'home_featured' | 'projects_page' | 'project_navigation'
  }

  external_link_click: {
    destination: string
    context: string
    type?: 'website' | 'github' | 'appstore' | 'demo' | 'changelog' | 'other'
  }

  // User preference events
  theme_toggle: {
    theme: 'light' | 'dark'
  }

  // Contact events
  email_copied: {
    location: 'header' | 'home'
  }

  // Content engagement
  tech_stack_interest: {
    technology: string
    project?: string
    location: 'project_detail' | 'projects_list' | 'home_featured'
  }

  content_engagement: {
    type:
      | 'photo_gallery'
      | 'craft_gallery'
      | 'typewriter'
      | 'location_hover'
      | 'video_play'
      | 'music_preview'
    duration?: number
    details?: string
  }

  // Special page events
  special_page_interaction: {
    page:
      | 'sleep'
      | 'inspiration'
      | 'steve'
      | 'closet'
      | 'fantasy'
      | 'mac'
      | 'rotation'
      | 'museum'
      | 'bricks'
    action: string
    details?: string
  }

  // API and errors
  api_error: {
    endpoint: string
    error: string
    status_code?: number
  }

  // Download events
  cv_download: {
    source: string
    timestamp: string
  }

  // Navigation flow
  project_navigation: {
    from_project: string
    to_project: string
    direction: 'next' | 'previous'
  }

  // Newsletter (for future use)
  newsletter_signup: {
    success: boolean
    error?: string
  }

  // 404s — which dead URLs people hit and where they came from
  page_not_found: {
    path: string
    referrer?: string
  }
}

export function useAnalytics() {
  // Mirrors loadAnalytics(): disabled on the server, on localhost, or when no
  // PostHog key is configured. React Compiler caches the result for this hook.
  const disabled = analyticsDisabled()

  const track = <T extends keyof AnalyticsEvent>(
    eventName: T,
    properties?: AnalyticsEvent[T],
  ) => {
    // Skip tracking when analytics is disabled (server, localhost, no key)
    if (disabled) {
      // eslint-disable-next-line no-console
      console.log('[Analytics Dev]', eventName, properties)
      return
    }

    try {
      captureEvent(eventName, {
        ...properties,
        timestamp: new Date().toISOString(),
      })
    } catch (error) {
      logger.error({ error }, '[Analytics Error]')
    }
  }

  const trackPageView = (
    pageName: string,
    properties?: Record<string, any>,
  ) => {
    // PostHog automatically tracks pageviews, but this allows custom pageview events
    if (disabled) {
      // eslint-disable-next-line no-console
      console.log('[Analytics Dev] Page View:', pageName, properties)
      return
    }

    try {
      captureEvent('$pageview', {
        page_name: pageName,
        ...properties,
      })
    } catch (error) {
      logger.error({ error }, '[Analytics Error]')
    }
  }

  const trackError = (error: Error, context?: Record<string, any>) => {
    track('api_error', {
      endpoint: context?.endpoint || 'unknown',
      error: error.message,
      status_code: context?.status_code,
    })
  }

  const identify = (userId: string, traits?: Record<string, any>) => {
    if (disabled) {
      // eslint-disable-next-line no-console
      console.log('[Analytics Dev] Identify:', userId, traits)
      return
    }

    try {
      identifyUser(userId, traits)
    } catch (error) {
      logger.error({ error }, '[Analytics Error]')
    }
  }

  return {
    track,
    trackPageView,
    trackError,
    identify,
  }
}
